import { defineStore } from 'pinia';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { buildApprovalPortalDestination, type ApprovalPortalDestination } from '~/lib/approvalPortalLinks';
import { resolveClientEmailOrigin } from '~/lib/tenantLinks';
import { toMillis } from '~/lib/timestamps';
import type { Approval, FileAttachment } from '~/types/models';
import { CORE_DATA_MAX_AGE_MS, chunkRequestValues, runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { richTextToPlainText } from '~/lib/richText';
import { normalizeApprovalAttachments } from '~/lib/approvalAttachments';
import { resolveFinalSignoffCompletion } from '~/lib/projectLifecycle';

export const useApprovalsStore = defineStore('approvals', {
    state: () => ({
        approvalsByProject: {} as Record<string, Approval[]>,
        loadingByProject: {} as Record<string, boolean>,
        loadedAtByProject: {} as Record<string, number>,
        approvalsError: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _sortApprovals(items: Approval[]): Approval[] {
            return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        async _resolveApprovalClient(clientId?: string) {
            if (!clientId) return null;
            const clientStore = useClientStore();
            return clientStore.clients.find((entry) => entry.id === clientId)
                || await clientStore.fetchClientById(clientId);
        },

        async _resolveApprovalProject(projectId: string) {
            const projectStore = useProjectStore();
            return projectStore.projects.find((entry) => entry.id === projectId)
                || (projectStore.currentProject?.id === projectId ? projectStore.currentProject : null)
                || await projectStore.fetchProjectById(projectId);
        },

        _buildApprovalPortalDestination(clientSlug: string, approvalId: string): ApprovalPortalDestination {
            const tenantStore = useTenantStore();
            const config = useRuntimeConfig();
            const origin = resolveClientEmailOrigin({
                tenant: tenantStore.currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });

            return buildApprovalPortalDestination({
                origin,
                tenantId: this._getTenantId(),
                clientSlug,
                approvalId,
            });
        },

        async _sendApprovalEmail(approval: Approval, options?: { subject?: string }): Promise<boolean> {
            const client = await this._resolveApprovalClient(approval.clientId);
            if (!client?.email || !client.slug) {
                return false;
            }

            const tenantStore = useTenantStore();
            const { actionLink } = this._buildApprovalPortalDestination(client.slug, approval.id);
            const project = await this._resolveApprovalProject(approval.projectId);

            await $fetch('/api/send-email', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId: tenantStore.currentTenantId,
                    to: client.email,
                    template: 'approval_requested',
                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                    clientName: client.name,
                    recipientName: client.contactName || client.name,
                    projectName: project?.name || approval.projectName,
                    projectSummary: 'Open the approval to review the instructions and respond.',
                    actionLink,
                    actionLabel: 'Review approval',
                    documentTitle: approval.stageName,
                    subject: options?.subject,
                },
            });

            return true;
        },

        async fetchApprovalsByProject(projectId: string, options: FreshRequestOptions = {}): Promise<Approval[]> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            if (!options.force
                && Object.prototype.hasOwnProperty.call(this.approvalsByProject, projectId)
                && Date.now() - (this.loadedAtByProject[projectId] || 0) < (options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS)) {
                return this.approvalsByProject[projectId] || [];
            }
            this.loadingByProject[projectId] = true;
            this.approvalsError = null;

            try {
                const approvalsRef = collection($db, `tenants/${tenantId}/approvals`);
                const q = query(approvalsRef, where('projectId', '==', projectId));
                const snap = await getDocs(q);
                const approvals = snap.docs.map((approvalDoc) => ({
                    id: approvalDoc.id,
                    ...approvalDoc.data(),
                } as Approval));

                this.approvalsByProject[projectId] = this._sortApprovals(approvals);
                this.loadedAtByProject[projectId] = Date.now();
                return this.approvalsByProject[projectId] || [];
            } catch (error: any) {
                this.approvalsError = error.message || 'Failed to load approvals.';
                console.error('Error fetching approvals:', error);
                return [];
            } finally {
                this.loadingByProject[projectId] = false;
            }
        },

        async fetchApprovalsForProjects(projectIds: string[], options: FreshRequestOptions = {}): Promise<Approval[]> {
            const normalizedProjectIds = Array.from(new Set(projectIds.map(id => String(id || '').trim()).filter(Boolean))).sort();
            if (normalizedProjectIds.length === 0) return [];

            const tenantId = this._getTenantId();
            const hasData = normalizedProjectIds.every(projectId => (
                Object.prototype.hasOwnProperty.call(this.approvalsByProject, projectId)
            ));
            const maxAgeMs = options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS;
            if (!options.force && hasData && normalizedProjectIds.every(projectId => (
                Date.now() - (this.loadedAtByProject[projectId] || 0) < maxAgeMs
            ))) {
                return normalizedProjectIds.flatMap(projectId => this.approvalsByProject[projectId] || []);
            }

            await runFreshRequest(`approvals:${tenantId}:projects:${normalizedProjectIds.join(',')}`, async () => {
                const { $db } = useNuxtApp();
                normalizedProjectIds.forEach((projectId) => {
                    this.loadingByProject[projectId] = true;
                    this.approvalsByProject[projectId] = [];
                });

                try {
                    const snapshots = await Promise.all(chunkRequestValues(normalizedProjectIds).map((projectIdChunk) => (
                        getDocs(query(
                            collection($db, `tenants/${tenantId}/approvals`),
                            where('projectId', 'in', projectIdChunk),
                        ))
                    )));
                    for (const snapshot of snapshots) {
                        for (const approvalDoc of snapshot.docs) {
                            const approval = { id: approvalDoc.id, ...approvalDoc.data() } as Approval;
                            this.approvalsByProject[approval.projectId] = this._sortApprovals([
                                ...(this.approvalsByProject[approval.projectId] || []),
                                approval,
                            ]);
                        }
                    }
                    normalizedProjectIds.forEach((projectId) => {
                        this.loadedAtByProject[projectId] = Date.now();
                    });
                } finally {
                    normalizedProjectIds.forEach((projectId) => {
                        this.loadingByProject[projectId] = false;
                    });
                }
            }, { ...options, maxAgeMs, hasData });

            return normalizedProjectIds.flatMap(projectId => this.approvalsByProject[projectId] || []);
        },

        async requestApproval(params: {
            projectId: string;
            clientId?: string;
            stageName: string;
            message?: string;
            attachments?: FileAttachment[];
        }): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.approvalsError = null;

            const authStore = useAuthStore();
            const userStore = useUserStore();
            const tenantStore = useTenantStore();
            const requestedBy = authStore.user?.uid || userStore.user?.id;
            if (!requestedBy) {
                throw new Error('You must be logged in to request approval.');
            }

            const payload = {
                tenantId,
                projectId: params.projectId,
                clientId: params.clientId,
                stageName: params.stageName,
                message: params.message?.trim() || '',
                attachments: normalizeApprovalAttachments(params.attachments),
                requestedBy,
                requestedByName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Unknown',
                status: 'pending' as const,
                createdAt: new Date(),
                respondedAt: null,
            };
            const plainMessage = richTextToPlainText(payload.message);

            const docRef = await addDoc(collection($db, `tenants/${tenantId}/approvals`), payload);
            const created = { id: docRef.id, ...payload } as Approval;
            const existing = this.approvalsByProject[params.projectId] || [];
            this.approvalsByProject[params.projectId] = this._sortApprovals([created, ...existing]);

            try {
                const { tenantPath } = useTenantPath();
                await dispatchNotification({
                    tenantId,
                    surface: 'tenant',
                    eventType: 'approval_requested',
                    title: `Approval requested for ${params.stageName}`,
                    body: plainMessage
                        ? plainMessage
                        : 'Client approval is now waiting on this project stage.',
                    recipients: [{ type: 'tenant_roles', roles: ['owner', 'admin'] }],
                    entityType: 'approval',
                    entityId: docRef.id,
                    projectId: params.projectId,
                    clientId: params.clientId,
                    actionPath: tenantPath({
                        path: `/projects/${params.projectId}`,
                        query: { tab: 'overview', approvalId: docRef.id },
                    }),
                    actionLabel: 'Open project',
                    actorId: requestedBy,
                    actorName: payload.requestedByName,
                }).catch((error) => {
                    console.warn('Tenant approval notification could not be created:', error);
                });

                if (params.clientId) {
                    const clientStore = useClientStore();
                    const client = clientStore.clients.find((entry) => entry.id === params.clientId) || await clientStore.fetchClientById(params.clientId);
                    const clientSlug = String(client?.slug || '').trim();

                    if (clientSlug) {
                        const project = await this._resolveApprovalProject(params.projectId);
                        const {
                            actionLink: approvalActionLink,
                            actionPath: approvalActionPath,
                        } = this._buildApprovalPortalDestination(clientSlug, docRef.id);
                        await Promise.allSettled([
                            dispatchNotification({
                                tenantId,
                                surface: 'client',
                                eventType: 'approval_requested',
                                title: `Approval waiting: ${params.stageName}`,
                                body: 'Open the approval to review the instructions and respond.',
                                recipients: [{
                                    type: 'portal_access',
                                    clientId: params.clientId,
                                    projectId: params.projectId,
                                    permission: 'respondApprovals',
                                }],
                                entityType: 'approval',
                                entityId: docRef.id,
                                projectId: params.projectId,
                                clientId: params.clientId,
                                actionPath: approvalActionPath,
                                actionLabel: 'Review approval',
                                actorId: requestedBy,
                                actorName: payload.requestedByName,
                                email: {
                                    template: 'approval_requested',
                                    actionLink: approvalActionLink,
                                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                                    clientName: client?.name,
                                    projectName: project?.name,
                                    projectSummary: 'Open the approval to review the instructions and respond.',
                                    documentTitle: params.stageName,
                                    actionLabel: 'Review approval',
                                },
                            }),
                            useMessagesStore().sendAutomatedMessage({
                                clientId: params.clientId,
                                clientName: client?.name,
                                subject: `Approval requested: ${params.stageName}`,
                                content: `${tenantStore.currentTenant?.name || 'The team'} has sent you an approval request for ${params.stageName}. Open the approval to review the instructions and respond.`,
                                actionPath: approvalActionPath,
                                actionLabel: 'Review approval',
                                entityType: 'approval',
                                entityId: docRef.id,
                            }),
                        ]);
                    }
                }
            } catch (notificationError) {
                console.warn('Failed to dispatch approval request notifications:', notificationError);
            }

            return docRef.id;
        },

        async sendApprovalReminder(approvalId: string, projectId: string): Promise<void> {
            const existing = (this.approvalsByProject[projectId] || []).find((approval) => approval.id === approvalId);
            if (!existing) {
                throw new Error('Approval not found.');
            }
            if (existing.status !== 'pending') {
                throw new Error('Only pending approvals can be reminded.');
            }
            if (!existing.clientId) {
                throw new Error('This approval is not linked to a client.');
            }

            const tenantStore = useTenantStore();
            const emailSent = await this._sendApprovalEmail(existing, {
                subject: `Reminder: ${tenantStore.currentTenant?.name || 'Vuey'} needs your approval`,
            });

            if (!emailSent) {
                throw new Error('The linked client needs a portal-enabled email address before a reminder can be sent.');
            }

            try {
                const authStore = useAuthStore();
                const userStore = useUserStore();
                const client = await this._resolveApprovalClient(existing.clientId);
                const clientSlug = String(client?.slug || '').trim();
                if (clientSlug) {
                    const { actionPath } = this._buildApprovalPortalDestination(clientSlug, existing.id);
                    await dispatchNotification({
                        tenantId: tenantStore.currentTenantId || this._getTenantId(),
                        surface: 'client',
                        eventType: 'approval_requested',
                        title: `Reminder: ${existing.stageName} still needs approval`,
                        body: richTextToPlainText(existing.message) || 'Your team has sent a reminder for this approval request.',
                        recipients: [{
                            type: 'portal_access',
                            clientId: existing.clientId,
                            projectId: existing.projectId,
                            permission: 'respondApprovals',
                        }],
                        entityType: 'approval',
                        entityId: existing.id,
                        projectId: existing.projectId,
                        clientId: existing.clientId,
                        actionPath,
                        actionLabel: 'Review approval',
                        actorId: authStore.user?.uid || userStore.user?.id,
                        actorName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName,
                        actorAvatarURL: userStore.user?.avatarURL,
                    });
                }
            } catch (notificationError) {
                console.warn('Approval reminder notification could not be created:', notificationError);
            }
        },

        async respondToApproval(
            approvalId: string,
            projectId: string,
            status: 'pending' | 'approved' | 'rejected',
            note?: string,
            message?: string,
        ): Promise<{ projectCompleted: boolean }> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.approvalsError = null;

            const authStore = useAuthStore();
            const userStore = useUserStore();
            const reviewedBy = authStore.user?.uid || userStore.user?.id;
            if (!reviewedBy) {
                throw new Error('You must be logged in to respond to approvals.');
            }

            const reviewedByName = authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Unknown';
            const now = new Date();
            const trimmedNote = note?.trim() || '';
            const trimmedMessage = message?.trim() || '';
            const existing = this.approvalsByProject[projectId] || [];
            const existingApproval = existing.find(item => item.id === approvalId);
            const shouldUpdateMessage = trimmedMessage !== String(existingApproval?.message || '').trim();
            const updateData: {
                status: 'pending' | 'approved' | 'rejected';
                reviewNote: string;
                message?: string;
                reviewedBy?: string;
                reviewedByName?: string;
                respondedAt?: Date;
            } = {
                status,
                reviewNote: trimmedNote,
                ...(shouldUpdateMessage ? { message: trimmedMessage } : {}),
            };

            if (status === 'approved' || status === 'rejected') {
                updateData.reviewedBy = reviewedBy;
                updateData.reviewedByName = reviewedByName;
                updateData.respondedAt = now;
            }

            await updateDoc(doc($db, `tenants/${tenantId}/approvals`, approvalId), updateData as any);

            this.approvalsByProject[projectId] = existing.map((item) =>
                item.id === approvalId
                    ? {
                        ...item,
                        ...updateData,
                        ...(status === 'approved' || status === 'rejected'
                            ? {
                                reviewedBy,
                                reviewedByName,
                                respondedAt: now,
                            }
                            : {}),
                    }
                    : item,
            );

            const project = await this._resolveApprovalProject(projectId);
            const completionUpdate = project && existingApproval
                ? resolveFinalSignoffCompletion(project, {
                    ...existingApproval,
                    status,
                }, now)
                : null;

            if (completionUpdate) {
                await useProjectStore().updateProject(projectId, completionUpdate);
            }

            return { projectCompleted: Boolean(completionUpdate) };
        },
    },
});
